import {
  type BankTransaction,
  FINANCIAL_ACCOUNT_KIND_LABELS,
  FINANCIAL_ACCOUNT_KINDS,
  type FinancialAccountKind,
  formatMinor,
  type PendingStatement,
} from '@hvac/contracts';
import { CLIENT_QUESTION_DEFAULT_PROMPTS } from '@hvac/contracts';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  PageHeader,
  Skeleton,
} from '@hvac/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import {
  accountsQuery,
  bankKeys,
  bankTransactionsQuery,
  confirmMatch,
  createAccount,
  dismissMatch,
  rematchBankLines,
  explainDeposit,
  explainPayment,
  importStatement,
  pendingStatementsQuery,
  rereadStatement,
  statementPreviewQuery,
} from '../../shared/api/bank-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';

import { UnmatchedLines } from './unmatched-lines.js';
import { askClientQuestion } from '../../shared/api/question-queries.js';

/**
 * The Bank page: statements in, reconciliation out.
 *
 * Two sections, in the order a person works them. Statements the router has
 * recognised wait at the top until an account is named, the preview shows
 * exactly what the server's parser read, because what you preview is
 * byte-for-byte what imports. Below, the matcher's proposals: each one carries
 * its evidence and two honest buttons. Money in green, money out in plain
 * charcoal, colour means meaning, and "you were paid" is the meaning worth
 * a colour.
 */

export function BankPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const accounts = useQuery(accountsQuery(organizationId));
  const statements = useQuery(pendingStatementsQuery(organizationId));
  const suggested = useQuery(bankTransactionsQuery(organizationId, { state: 'suggested' }));
  const [askedLink, setAskedLink] = useState<string | null>(null);
  const [askedCopied, setAskedCopied] = useState(false);
  const unmatched = useQuery(
    bankTransactionsQuery(organizationId, { state: 'unmatched', limit: 50 }),
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: bankKeys.all(organizationId) });
  };
  const onError = (caught: unknown) =>
    setError(caught instanceof Error ? caught.message : 'That did not work.');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Bank"
        description="Statements import each line exactly once. Matching is proposed with its evidence, and becomes real only when you confirm it."
      />

      {error !== null ? <Alert tone="danger">{error}</Alert> : null}

      {statements.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : (statements.data ?? []).length === 0 ? null : (
        <section className="flex flex-col gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
            Waiting to import
          </h2>
          {(statements.data ?? []).map((statement) => (
            <StatementCard
              key={statement.documentId}
              organizationId={organizationId}
              statement={statement}
              accounts={accounts.data ?? []}
              onDone={() => {
                setError(null);
                invalidate();
              }}
              onError={onError}
            />
          ))}
        </section>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-content-subtle">
          Proposed matches
        </h2>
        {suggested.isPending ? (
          <Skeleton className="h-20 w-full" />
        ) : (suggested.data ?? []).length === 0 ? (
          <Card>
            <CardBody className="py-8 text-center text-sm text-content-muted">
              Nothing waiting on you. Proposals appear here when a statement line and a record agree
              on the money.
            </CardBody>
          </Card>
        ) : (
          (suggested.data ?? []).map((line) => (
            <ProposalCard
              key={line.id}
              organizationId={organizationId}
              line={line}
              onDone={() => {
                setError(null);
                invalidate();
              }}
              onError={onError}
            />
          ))
        )}
      </section>

      {askedLink === null ? null : (
        <Alert tone="success" title="The question is ready to send">
          <p>
            Send this link to the client by text or email. They photograph the receipt or say what
            it was, with nothing to sign in to, and it lands in the Inbox.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="max-w-full break-all rounded bg-surface-sunken px-2 py-1 text-xs">
              {askedLink}
            </code>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                navigator.clipboard
                  .writeText(askedLink)
                  .then(() => setAskedCopied(true))
                  .catch(() => setAskedCopied(false));
              }}
            >
              {askedCopied ? 'Copied' : 'Copy link'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAskedLink(null)}>
              Done
            </Button>
          </div>
        </Alert>
      )}

      <UnmatchedLines
        lines={unmatched.data ?? []}
        onAsk={(line) => {
          askClientQuestion(organizationId, {
            kind: 'missing_receipt',
            prompt: CLIENT_QUESTION_DEFAULT_PROMPTS.missing_receipt,
            bankTransactionId: line.id,
          })
            .then((asked) => {
              setAskedLink(asked.link);
              setAskedCopied(false);
            })
            .catch(onError);
        }}
        onExplainPayment={(line, as, note) => {
          explainPayment(organizationId, line.id, note.trim() === '' ? { as } : { as, note })
            .then(invalidate)
            .catch(onError);
        }}
        onIgnore={(line) => {
          dismissMatch(organizationId, line.id, { outcome: 'ignored' })
            .then(invalidate)
            .catch(onError);
        }}
        onRematch={() => {
          rematchBankLines(organizationId).then(invalidate).catch(onError);
        }}
        onExplain={(line, as, note) => {
          explainDeposit(organizationId, line.id, note.trim() === '' ? { as } : { as, note })
            .then(invalidate)
            .catch(onError);
        }}
      />

      <AccountsSection
        organizationId={organizationId}
        onDone={() => {
          setError(null);
          invalidate();
        }}
        onError={onError}
      />
    </div>
  );
}

/* ---------------------------------------------------------------------- */

function StatementCard({
  organizationId,
  statement,
  accounts,
  onDone,
  onError,
}: {
  organizationId: string;
  statement: PendingStatement;
  accounts: { id: string; name: string }[];
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const [chosenAccountId, setChosenAccountId] = useState('');
  const reading = statement.reading ?? null;
  const readable = reading === null || reading.status === 'balanced';
  const preview = useQuery({
    ...statementPreviewQuery(organizationId, statement.documentId),
    enabled: readable,
  });
  // The account the statement names is preselected; a choice overrides it.
  const accountId =
    chosenAccountId !== '' ? chosenAccountId : (preview.data?.suggestedAccountId ?? '');
  const proof = preview.data?.proof ?? null;

  const run = useMutation({
    mutationFn: () =>
      importStatement(organizationId, statement.documentId, { financialAccountId: accountId }),
    onSuccess: onDone,
    onError,
  });
  const reread = useMutation({
    mutationFn: () => rereadStatement(organizationId, statement.documentId),
    onSuccess: onDone,
    onError,
  });

  const accountLabel =
    reading?.accountKind === 'credit_card'
      ? 'Credit card'
      : reading?.accountKind === 'bank'
        ? 'Bank account'
        : null;

  return (
    <Card>
      <CardHeader
        title={statement.filename ?? 'Statement'}
        description={
          !readable
            ? (accountLabel ?? 'Statement') +
              (reading.accountMask === null ? '' : ` ending ${reading.accountMask}`) +
              `, read ${String(reading.attempt)} time(s)`
            : preview.data === undefined
              ? 'Reading the file…'
              : `${preview.data.rowCount} line(s)` +
                (preview.data.periodStart === null
                  ? ''
                  : `, ${preview.data.periodStart} to ${preview.data.periodEnd ?? ''}`) +
                (preview.data.skippedCount > 0
                  ? `, ${preview.data.skippedCount} unreadable row(s) will be skipped`
                  : '') +
                (preview.data.accountMask === null || preview.data.accountMask === undefined
                  ? ''
                  : `, ${accountLabel ?? 'account'} ending ${preview.data.accountMask}`)
        }
      />
      <CardBody className="flex flex-col gap-3">
        {!readable ? (
          <>
            <Alert
              tone="warning"
              title={
                reading.status === 'unbalanced'
                  ? 'This statement does not balance, so it is not imported.'
                  : 'This statement could not be read.'
              }
            >
              {reading.failure ?? 'The reading produced nothing usable.'} Read it again, or import a
              CSV of the same period from the bank.
            </Alert>
            <div>
              <Button
                variant="secondary"
                onClick={() => reread.mutate()}
                isLoading={reread.isPending}
              >
                Read again
              </Button>
            </div>
          </>
        ) : preview.data === undefined ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <div className="overflow-x-auto rounded-[var(--radius-control)] bg-surface-sunken p-2">
            <table className="w-full text-xs">
              <tbody>
                {preview.data.sample.map((row, index) => (
                  <tr key={index}>
                    <td className="px-2 py-1 whitespace-nowrap">{row.postedOn}</td>
                    <td className="max-w-0 truncate px-2 py-1 text-content-muted">
                      {row.description}
                    </td>
                    <td className="tabular px-2 py-1 text-right">{formatMinor(row.amountMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {proof?.balanced ? (
          <p className="text-xs text-content-muted">
            Proved: opening {formatMinor(proof.openingMinor ?? '0')} plus every line equals closing{' '}
            {formatMinor(proof.closingMinor ?? '0')}
            {proof.pagesChecked > 0 ? `, on ${String(proof.pagesChecked)} page(s) checked` : ''}.
          </p>
        ) : null}

        <div className="flex flex-wrap items-end gap-3">
          <Field label="Import into" className="min-w-56">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={accountId}
                onChange={(event) => setChosenAccountId(event.target.value)}
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                <option value="">Choose an account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Button
            onClick={() => run.mutate()}
            disabled={accountId === '' || preview.data === undefined || !readable}
            isLoading={run.isPending}
          >
            Import
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ProposalCard({
  organizationId,
  line,
  onDone,
  onError,
}: {
  organizationId: string;
  line: BankTransaction;
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const proposal = line.proposal;
  if (proposal === null) return null;

  const act = (fn: () => Promise<unknown>) => {
    fn().then(onDone).catch(onError);
  };

  return (
    <Card>
      <CardBody className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-sm text-content">
            <span className="text-content-muted">{line.postedOn} · </span>
            {line.description}
          </p>
          <p className="tabular text-sm font-medium">{formatMinor(line.amountMinor)}</p>
        </div>

        <div className="rounded-[var(--radius-control)] bg-surface-sunken px-3 py-2 text-sm">
          <p className="text-content">
            {proposal.vendorName ?? 'A record'} · {proposal.transactionDate} ·{' '}
            <span className="tabular">{formatMinor(proposal.grossAmountMinor)}</span>
          </p>
          {proposal.reasoning === null ? null : (
            <p className="mt-0.5 text-xs text-content-muted">{proposal.reasoning}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() =>
              act(() =>
                confirmMatch(organizationId, line.id, { transactionId: proposal.transactionId }),
              )
            }
          >
            Confirm
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              act(() => dismissMatch(organizationId, line.id, { outcome: 'unmatched' }))
            }
          >
            Not this one
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => act(() => dismissMatch(organizationId, line.id, { outcome: 'ignored' }))}
            title="Not a business transaction"
          >
            Ignore
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function AccountsSection({
  organizationId,
  onDone,
  onError,
}: {
  organizationId: string;
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const accounts = useQuery(accountsQuery(organizationId));
  const [name, setName] = useState('');
  const [kind, setKind] = useState<FinancialAccountKind>('chequing');

  const add = useMutation({
    mutationFn: () => createAccount(organizationId, { kind, name: name.trim() }),
    onSuccess: () => {
      setName('');
      onDone();
    },
    onError,
  });

  return (
    <Card>
      <CardHeader
        title="Accounts"
        description="The accounts statements import into. Adding one is a settings right, and the books never depend on an account existing."
      />
      <CardBody className="flex flex-col gap-3">
        {(accounts.data ?? []).length === 0 ? (
          <p className="text-sm text-content-muted">
            No accounts yet. Add the chequing account or card the statements come from.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {(accounts.data ?? []).map((account) => (
              <li
                key={account.id}
                className="rounded-full border border-border px-3 py-1 text-sm text-content"
              >
                {account.name}
                <span className="ml-1.5 text-xs text-content-subtle">
                  {FINANCIAL_ACCOUNT_KIND_LABELS[account.kind]}
                  {account.accountMask === null ? '' : ` ····${account.accountMask}`}
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <Field label="Account name" className="min-w-56">
            {({ inputProps }) => (
              <Input
                {...inputProps}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Operating chequing"
              />
            )}
          </Field>
          <Field label="Kind">
            {({ inputProps }) => (
              <select
                {...inputProps}
                value={kind}
                onChange={(event) => setKind(event.target.value as FinancialAccountKind)}
                className="h-10 rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 text-sm"
              >
                {FINANCIAL_ACCOUNT_KINDS.map((value) => (
                  <option key={value} value={value}>
                    {FINANCIAL_ACCOUNT_KIND_LABELS[value]}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Button
            variant="secondary"
            onClick={() => add.mutate()}
            disabled={name.trim() === ''}
            isLoading={add.isPending}
          >
            Add account
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
